import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ChefProfiles = () => {
  const chefs = [
    {
      name: "Hassan Mahmoud",
      title: "Founder & Head Chef",
      experience: "37 years",
      specialty: "Traditional Lamb Kebabs & Spice Blends",
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      story: `Hassan's culinary journey began in his grandmother's kitchen in Antalya, where he learned the ancient art of spice blending and meat preparation. His expertise in traditional Ottoman cooking techniques and his ability to source the finest ingredients have made Kebab Palace a destination for authentic Mediterranean flavors. Hassan still personally oversees every spice blend and trains each new chef in the traditional methods passed down through his family.`,
      achievements: [
        "Certified Master Chef - Istanbul Culinary Institute",
        "Mediterranean Cuisine Excellence Award 2019",
        "Community Chef of the Year 2021"
      ],
      signature: "Grandmother\'s Secret Spice Lamb Kebab"
    },
    {
      name: "Omar Mahmoud",
      title: "Executive Chef & Operations Manager",
      experience: "26 years",
      specialty: "Modern Mediterranean Fusion & Catering",
      image: "https://images.unsplash.com/photo-1607631568010-a87245c0daf8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      story: `Omar bridges the gap between traditional techniques and modern culinary innovation. After completing his formal training in Istanbul and working in renowned Mediterranean restaurants across Europe, he returned to expand Kebab Palace's offerings while maintaining authenticity. His expertise in large-scale catering and menu development has helped the restaurant serve thousands of satisfied customers while preserving the intimate, family-style experience.`,
      achievements: [
        "Culinary Arts Degree - Le Cordon Bleu Istanbul",
        "Certified Catering Professional",
        "Best Innovation in Traditional Cuisine 2022"
      ],
      signature: "Pomegranate Glazed Chicken Kebab"
    },
    {
      name: "Fatima Al-Rashid",
      title: "Pastry Chef & Dessert Specialist",
      experience: "15 years",
      specialty: "Traditional Mediterranean Desserts",
      image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      story: `Fatima joined our family after completing her specialized training in traditional Middle Eastern and Mediterranean pastries. Her delicate touch with phyllo dough and mastery of honey-based desserts have elevated our dessert menu to new heights. She sources authentic ingredients like pistachios from Gaziantep and orange blossom water from Lebanon to ensure every dessert tells an authentic story of Mediterranean sweetness.`,
      achievements: [
        "Specialized Pastry Certification - Damascus Culinary School",
        "Best Traditional Dessert Menu 2023",
        "Mediterranean Pastry Competition Winner"
      ],
      signature: "Rose Water Baklava with Antep Pistachios"
    },
    {
      name: "Dimitri Kostas",
      title: "Grill Master & Meat Specialist",
      experience: "22 years",
      specialty: "Charcoal Grilling & Meat Preparation",
      image: "https://images.unsplash.com/photo-1566554273541-37a9ca77b91b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      story: `Dimitri's expertise with the charcoal grill is legendary among our team. Having learned traditional Greek grilling techniques from his father and grandfather, he brings a unique perspective to our Mediterranean offerings. His understanding of temperature control, timing, and the subtle art of charcoal cooking ensures that every kebab achieves the perfect balance of smoky flavor and tender texture that our customers have come to expect.`,
      achievements: [
        "Master Grill Certification - Athens Culinary Academy",
        "Perfect Grill Technique Award 2020",
        "Mediterranean Grilling Championship Finalist"
      ],
      signature: "Charcoal-Grilled Mixed Grill Platter"
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Meet Our Culinary Masters
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Our talented team of chefs brings together decades of experience, traditional training, and passionate dedication to authentic Mediterranean cuisine.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {chefs.map((chef, index) => (
          <motion.div
            key={chef.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="card-surface hover:shadow-warm-lg transition-organic"
          >
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Chef Photo */}
              <div className="flex-shrink-0">
                <div className="relative w-32 h-32 mx-auto sm:mx-0">
                  <Image
                    src={chef.image}
                    alt={chef.name}
                    className="w-full h-full object-cover rounded-full shadow-warm"
                  />
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-warm">
                    <Icon name="ChefHat" size={20} className="text-white" />
                  </div>
                </div>
              </div>

              {/* Chef Info */}
              <div className="flex-1">
                <div className="text-center sm:text-left mb-4">
                  <h3 className="font-headline text-2xl font-bold text-text-primary mb-1">
                    {chef.name}
                  </h3>
                  <p className="font-cta text-primary font-semibold mb-2">
                    {chef.title}
                  </p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-sm text-text-secondary">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={16} />
                      <span>{chef.experience}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={16} />
                      <span>{chef.specialty}</span>
                    </div>
                  </div>
                </div>

                <p className="font-body text-text-secondary leading-relaxed mb-6">
                  {chef.story}
                </p>

                {/* Signature Dish */}
                <div className="bg-accent-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Crown" size={18} className="text-accent-600" />
                    <span className="font-cta font-semibold text-accent-700">
                      Signature Dish
                    </span>
                  </div>
                  <p className="font-body text-accent-800 font-medium">
                    {chef.signature}
                  </p>
                </div>

                {/* Achievements */}
                <div>
                  <h4 className="font-cta font-semibold text-text-primary mb-3 flex items-center space-x-2">
                    <Icon name="Award" size={18} />
                    <span>Achievements</span>
                  </h4>
                  <ul className="space-y-2">
                    {chef.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                        <Icon name="Check" size={16} className="text-success-500 mt-0.5 flex-shrink-0" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Team Philosophy */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="mt-16 text-center"
      >
        <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-primary-50 to-accent-50">
          <Icon name="Users" size={48} className="text-primary mx-auto mb-6" />
          <h3 className="font-headline text-3xl font-bold text-text-primary mb-6">
            Our Culinary Philosophy
          </h3>
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-6">
            Together, our team shares a common commitment: to honor traditional Mediterranean cooking methods while creating memorable dining experiences. Every dish that leaves our kitchen represents not just our individual expertise, but our collective passion for authentic flavors and warm hospitality.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center space-x-2 text-primary">
              <Icon name="Heart" size={16} />
              <span className="font-medium">Passion for Authenticity</span>
            </div>
            <div className="flex items-center space-x-2 text-secondary">
              <Icon name="Users" size={16} />
              <span className="font-medium">Family Tradition</span>
            </div>
            <div className="flex items-center space-x-2 text-accent-600">
              <Icon name="Star" size={16} />
              <span className="font-medium">Excellence in Every Dish</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ChefProfiles;